/**
 * Oynatıcı öğesini kaynağa **bağlar**.
 *
 * Bu kanca, oynatma tarafındaki hataların **üçünün** de kaynağıydı; bu yüzden
 * ayrı dosyada ve testli.
 *
 * ## 1. Oynatma hiç başlamıyordu
 *
 * Oynatma hazır değilken `WatchScreen` `<Preparing>` döndürür, `<video>` DOM'da
 * **yoktur**. Klasik `useEffect([videoRef], ...)` deseni erken döner ve bir daha
 * çalışmaz. Çözüm: bağımlılık **öğenin kendisi** (`videoRef.element`).
 *
 * ## 2. Oynatma 12. saniyede kendiliğinden duruyordu
 *
 * `status.state` her oynatmada değişir (`running` → `ready`) ve bu, **aynı
 * URL'yi taşıyan yeni** bir `source` nesnesi üretir. Etiket `source` **nesnesine**
 * bağlıysa her durum değişiminde yeniden kurulur; temizlik `detachSource`
 * çağırır, `MediaSource` `<video>`'dan ayrılır ve **oynatma durur**.
 *
 * Gerçek sunucuda ölçüm: oynatıcı `0:12`'de donuyor, hls.js 144. segmente
 * kadar çekmiş (ağ sağlam, tampon dolu), konsolda hata yok.
 *
 * Çözüm: etiket `source.url` **ilkeline** bağlıdır. Aynı URL için yeniden
 * bağlanmaz; temizlik de yalnızca **gerçekten ayrılacaksa** ayırır.
 *
 * ## 3. Yeniden deneme döngüsü
 *
 * Bu kancaya "bağlanmazsa bir kez daha dene" eklenmişti (4 saniyede bir).
 * `MANIFEST_PARSED` bildirimi gelmediği için döngü sürekli tetikleniyor, her
 * seferinde oynatıcı ayrılıp yeniden bağlanıyor ve kaldığı konuma geri
 * atılıyordu — yani **1 numaralı hatanın kendisi**. Döngü kaldırıldı.
 */
import { useEffect, useRef } from 'react';
import {
  attachSource,
  createResumeSeek,
  detachSource,
  type HlsConstructor,
  type PlaybackEvent,
  type PlaybackSource,
} from './playback.ts';
import type { ElementRef } from './useElementRef.ts';

export interface UseAttachOptions {
  setNotice: (message: string | null) => void;
  setError: (message: string | null) => void;
  /** Kullanıcı yeniden denediğinde aynı URL'yi zorla yeniden bağlar. */
  retryKey?: number;
  /** Yalnız testler: sahte `hls.js` kurucusu. */
  hlsFactory?: HlsConstructor;
}

export function useAttach(
  videoRef: ElementRef<HTMLVideoElement>,
  source: PlaybackSource | null,
  resumeMs: number,
  options: UseAttachOptions,
): void {
  const { setNotice, setError, retryKey = 0, hlsFactory } = options;

  /** `source` nesnesinin **son** değeri (nesne referansı). */
  const sourceRef = useRef(source);
  useEffect(() => {
    sourceRef.current = source;
  }, [source]);

  /** Yalnız URL: etiket bunu bağımlılık olarak alır (nesne değil). */
  const sourceUrl = source?.url ?? null;

  /** Bağlanılan URL; aynı URL için ikinci kez bağlanmayı önler. */
  const attachedUrlRef = useRef<string | null>(null);
  const lastRetryKeyRef = useRef(retryKey);

  useEffect(() => {
    // `videoRef.element` bağımlılık olarak veriliyor: `<video>` oynatma hazır
    // olduğunda DOM'a girer, `element` güncellenir ve bu etiket ancak o zaman
    // kurulur. `ref.current` okumak yanlış olurdu — etiket `null` görüp erken
    // dönerdi ve bir daha çalışmazdı.
    if (lastRetryKeyRef.current !== retryKey) {
      lastRetryKeyRef.current = retryKey;
      attachedUrlRef.current = null;
    }
    const video = videoRef.element;
    if (video === null || sourceUrl === null) return;

    // Aynı URL: zaten bağlıyız. Dokunmak oynatmayı bozardı — temizlik
    // MediaSource'u ayırıp oynatmayı durduruyor.
    if (attachedUrlRef.current === sourceUrl) return;
    attachedUrlRef.current = sourceUrl;

    const current = sourceRef.current;
    if (current === null) return;

    let cancelled = false;

    const onEvent = (event: PlaybackEvent): void => {
      if (event.kind === 'recovering') setNotice(event.message);
      else if (event.kind === 'error') {
        setNotice(null);
        setError(event.message);
      }
    };

    const resumeAt = resumeMs / 1000;

    // `attachSource` asenkrondur: HLS gerekiyorsa `hls.js` dinamik yüklenir.
    void attachSource(video, current, onEvent, hlsFactory, resumeAt)
      .then((result) => {
        if (cancelled) return;
        // Hata **görünür** olmalı. Sessizce beklemek "sunucu yavaş" izlenimi
        // veriyordu; desteklenmeyen tarayıcı ya da yüklenemeyen hls.js hiç
        // bildirilmiyordu.
        if (result.error !== undefined) setError(result.error);
      })
      .catch(() => {
        if (!cancelled) setError('Oynatıcı hazırlanamadı.');
      });

    // Kademeli playlist'te `loadedmetadata` **tekrarlanır** (playlist büyüdükçe
    // süre değişir, hls.js medyayı yeniden bildirir); koşulsuz atlayan bir geri
    // çağırma oynatıcıyı kaldığı konuma **çiveler`. Düzeltme ve gerekçe:
    // `createResumeSeek` (`playback.ts`).
    const onLoaded = createResumeSeek(video, resumeAt, () => setNotice('Oynatmak için dokunun'));
    video.addEventListener('loadedmetadata', onLoaded);

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onLoaded);

      /*
       * **Yalnızca gerçekten ayrılacaksa ayır.**
       *
       * Kaynak değiştiyse ayırmaya gerek yok: `attachSource` zaten başta
       * `detachSource` çağırır. Öğe ayrıldıysa ayırmak zorunlu (sızıntı).
       */
      const elementChanged = videoRef.element !== video;
      const urlChanged = attachedUrlRef.current !== sourceUrl;

      if (elementChanged || urlChanged) {
        detachSource(video);
        attachedUrlRef.current = null;
      }
    };
  }, [videoRef.element, sourceUrl, resumeMs, retryKey, setNotice, setError, hlsFactory]);
}
